import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TIME_FORMAT } from '@/app/constants/date'
import { GpsDataModel } from '@/app/models/gpsDataModel'
import { GpsFromTachographModel } from '@/app/models/gpsFromTachographModel'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { GpsDataType } from '@/app/constants/gpsDataType'
import { GeneratedTripCountrySourceType } from '@/app/constants/configuration/generatedTripCountrySourceType'
import { useQueryConfigurationBusinessTrips } from '@/lib/api/company/configuration/businessTrips/useQueryConfigurationBusinessTrips'
import { DateField } from '@/ui/form/controls/DateField'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { getGpsDataTypeName } from '@/app/common/converters/gps/gpsDataTypeToTextConverter'
import { getGpsDataStatusName } from '@/app/common/converters/gps/gpsDataStatusToTextConverter'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { toast } from '@/lib/toasts/api'
import { useGlobalDateRange } from './useGlobalDateRange'

interface BusinessTripEditGpsViewProps {
  tripData: any
  companyId: string
  driverId: string
  onCancel: () => void
  onSave: (events: EventModel[]) => void
}

const GpsColumns: TableHeaderColumn[] = [
  { title: 'ŹRÓDŁO', key: 'source', type: 'string', sort: false },
  { title: 'TYP', key: 'type', type: 'string', sort: false },
  { title: 'DATA PRZYJAZDU', key: 'arriveDate', type: 'string', sort: false },
  { title: 'DATA WYJAZDU', key: 'leaveDate', type: 'string', sort: false },
  { title: 'KRAJ', key: 'foreignCountry', type: 'string', sort: false },
  { title: 'MIASTO', key: 'city', type: 'string', sort: false },
  { title: 'TRANSIT', key: 'transit', type: 'string', sort: false },
  { title: 'STATUS', key: 'status', type: 'string', sort: false },
]

type ImportSources = {
  tacho: boolean
  gps: boolean
}

type ImportRow = {
  key: string
  date: Moment | null
  checked: boolean
} & (
  | { source: 'gps'; gps: GpsDataModel }
  | { source: 'tacho'; tacho: GpsFromTachographModel }
)

const sourcesFromConfiguration = (
  countrySource: GeneratedTripCountrySourceType,
): ImportSources => ({
  tacho:
    countrySource === GeneratedTripCountrySourceType.FromTacho ||
    countrySource === GeneratedTripCountrySourceType.MixMode,
  gps:
    countrySource === GeneratedTripCountrySourceType.FromGps ||
    countrySource === GeneratedTripCountrySourceType.MixMode,
})

const BusinessTripEditGpsView: React.FC<BusinessTripEditGpsViewProps> = ({
  tripData,
  companyId,
  driverId,
  onCancel,
  onSave,
}) => {
  const { t } = useLocalize()
  const { getGpsData, getGpsFromTachograph } = useBusinessTripService()

  const [rows, setRows] = React.useState<ImportRow[]>([])
  const [isLoading, setIsLoading] = React.useState(false)

  const globalDateRange = useGlobalDateRange()

  const [dateFrom, setDateFrom] = React.useState<string>(globalDateRange.from)
  const [dateTo, setDateTo] = React.useState<string>(globalDateRange.to)

  const { data: businessTripConfiguration, isLoading: isConfigurationLoading } =
    useQueryConfigurationBusinessTrips(companyId)
  const [sources, setSources] = React.useState<ImportSources>({
    tacho: false,
    gps: false,
  })
  const sourcesTouched = React.useRef(false)

  React.useEffect(() => {
    if (isConfigurationLoading || sourcesTouched.current) return

    setSources(
      sourcesFromConfiguration(
        businessTripConfiguration?.generatedTripCountrySource ??
          GeneratedTripCountrySourceType.FromGps,
      ),
    )
  }, [isConfigurationLoading, businessTripConfiguration?.generatedTripCountrySource])

  const setSource = (source: keyof ImportSources, checked: boolean) => {
    sourcesTouched.current = true
    setSources((prev) => ({ ...prev, [source]: checked }))
  }

  const allChecked = rows.length > 0 && rows.every((row) => row.checked)

  React.useEffect(() => {
    const fetchRows = async () => {
      if (isConfigurationLoading) return

      if (!driverId || !dateFrom || !dateTo || (!sources.gps && !sources.tacho)) {
        setRows([])
        return
      }

      try {
        setIsLoading(true)

        const [gpsData, tachographData] = await Promise.all([
          sources.gps
            ? getGpsData({
                companyId,
                driverId,
                startTime: dateFrom,
                endTime: dateTo,
              })
            : Promise.resolve([]),
          sources.tacho
            ? getGpsFromTachograph({
                companyId,
                driverId,
                startTime: dateFrom,
                endTime: dateTo,
              })
            : Promise.resolve([]),
        ])

        const gpsRows: ImportRow[] = (gpsData || []).map(
          (item: GpsDataModel) => ({
            key: `gps-${item.id}`,
            source: 'gps',
            gps: item,
            date: item.arriveDate
              ? moment(item.arriveDate)
              : item.leaveDate
                ? moment(item.leaveDate)
                : null,
            checked: false,
          }),
        )

        const tachographRows: ImportRow[] = (tachographData || []).map(
          (item: GpsFromTachographModel, index: number) => ({
            key: `tacho-${item.id ?? index}`,
            source: 'tacho',
            tacho: item,
            date: item.date ? moment(item.date) : null,
            checked: false,
          }),
        )

        setRows(
          [...gpsRows, ...tachographRows].sort((a, b) => {
            if (!a.date) return 1
            if (!b.date) return -1
            return a.date.valueOf() - b.date.valueOf()
          }),
        )
      } catch (err: any) {
        console.error('Error fetching border crossing data:', err)
        // Only show error if it's an actual API error (has response), not a validation issue
        if (err?.response) {
          toast.error({
            text: err?.response?.data?.message || err?.message || t('errorCodes.retrieving-gps-data-failed'),
          })
        }
        setRows([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchRows()
  }, [
    companyId,
    driverId,
    dateFrom,
    dateTo,
    sources.gps,
    sources.tacho,
    isConfigurationLoading,
  ])

  const handleToggleAll = (checked: boolean) => {
    setRows((prev) => prev.map((row) => ({ ...row, checked })))
  }

  const handleToggleItem = (key: string, checked: boolean) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, checked } : row))
    )
  }

  const addItemBasedOnGpsData = (
    date: Moment | null,
    country: any,
    city: string | null,
    tripEvent: TripEventType,
    transit: boolean,
    existingItems: Moment[],
    lp: number
  ): EventModel | null => {
    if (!date) return null

    if (existingItems.length > 0) {
      for (const item of existingItems) {
        if (item.format(TIME_FORMAT) === date.format(TIME_FORMAT)) {
          return null
        }
      }
    }

    return {
      date,
      isTransit: transit,
      tripEvent,
      country: country || null,
      city: city || undefined,
      lp,
    }
  }

  const buildEventsFromGps = (
    selectedItems: GpsDataModel[],
    existingItems: Moment[]
  ): EventModel[] => {
    let beginItemExists = false
    const events: EventModel[] = []

    for (let i = 0; i < selectedItems.length; i++) {
      const gpsDataCurrent = selectedItems[i]
      const gpsDataPrevious = i - 1 >= 0 ? selectedItems[i - 1] : null
      const gpsDataNext = i + 1 < selectedItems.length ? selectedItems[i + 1] : null

      const arriveDateUniversal = gpsDataCurrent.arriveDate
        ? moment(gpsDataCurrent.arriveDate)
        : null
      const leaveDateUniversal = gpsDataCurrent.leaveDate
        ? moment(gpsDataCurrent.leaveDate)
        : null

      if (!beginItemExists && gpsDataCurrent.isBeginOrEnd) {
        const event = addItemBasedOnGpsData(
          arriveDateUniversal,
          gpsDataCurrent.foreignCountry,
          gpsDataCurrent.city,
          TripEventType.Begin,
          gpsDataCurrent.transit,
          existingItems,
          i
        )
        if (event) {
          events.push(event)
          existingItems.push(moment(arriveDateUniversal))
        }
        beginItemExists = true
      } else {
        if (gpsDataCurrent.type !== GpsDataType.DepartureFromBase) {
          const event = addItemBasedOnGpsData(
            arriveDateUniversal,
            gpsDataCurrent.foreignCountry,
            gpsDataPrevious ? gpsDataPrevious.city : null,
            TripEventType.ChangeCountry,
            gpsDataCurrent.transit,
            existingItems,
            i
          )
          if (event) {
            events.push(event)
            existingItems.push(moment(arriveDateUniversal))
          }
        }

        if (
          gpsDataCurrent.isBeginOrEnd ||
          gpsDataCurrent.type === GpsDataType.ReturnToBase ||
          gpsDataCurrent.type === GpsDataType.DepartureFromBase
        ) {
          const event = addItemBasedOnGpsData(
            leaveDateUniversal,
            gpsDataCurrent.foreignCountry,
            gpsDataCurrent.city,
            gpsDataCurrent.type !== GpsDataType.DepartureFromBase
              ? TripEventType.Finish
              : TripEventType.Begin,
            gpsDataCurrent.transit,
            existingItems,
            i
          )
          if (event) {
            events.push(event)
            existingItems.push(moment(leaveDateUniversal))
          }
        } else if (
          gpsDataCurrent.isMinimalWages &&
          leaveDateUniversal &&
          (!gpsDataNext?.arriveDate ||
            leaveDateUniversal.format('HH:mm') !==
              moment(gpsDataNext.arriveDate).format('HH:mm'))
        ) {
          const event = addItemBasedOnGpsData(
            leaveDateUniversal,
            null,
            null,
            TripEventType.ChangeCountry,
            false,
            existingItems,
            i
          )
          if (event) {
            events.push(event)
            existingItems.push(moment(leaveDateUniversal))
          }
        }
      }
    }

    return events
  }

  const buildEventsFromTachograph = (
    selectedItems: GpsFromTachographModel[],
    existingItems: Moment[]
  ): EventModel[] => {
    const events: EventModel[] = []

    for (let i = 0; i < selectedItems.length; i++) {
      const tachographCurrent = selectedItems[i]
      if (!tachographCurrent.country || !tachographCurrent.date) continue

      const event = addItemBasedOnGpsData(
        moment(tachographCurrent.date),
        tachographCurrent.country,
        null,
        TripEventType.ChangeCountry,
        tachographCurrent.isTransit || false,
        existingItems,
        i
      )
      if (event) {
        events.push(event)
        existingItems.push(moment(tachographCurrent.date))
      }
    }

    return events
  }

  const handleSave = () => {
    const selectedRows = rows.filter((row) => row.checked)
    if (selectedRows.length === 0) {
      toast.error({
        text: t('newDelegation.no-items-selected'),
      })
      return
    }

    const existingItems: Moment[] = (tripData.events || []).map((e: any) =>
      moment(e.date)
    )

    const selectedGps = selectedRows
      .filter((row): row is ImportRow & { source: 'gps'; gps: GpsDataModel } =>
        row.source === 'gps'
      )
      .map((row) => row.gps)
    const selectedTachograph = selectedRows
      .filter(
        (row): row is ImportRow & { source: 'tacho'; tacho: GpsFromTachographModel } =>
          row.source === 'tacho'
      )
      .map((row) => row.tacho)

    const events = [
      ...buildEventsFromGps(selectedGps, existingItems),
      ...buildEventsFromTachograph(selectedTachograph, existingItems),
    ]

    const filteredEvents = events.filter((e) => e != null)
    if (filteredEvents.length > 0) {
      onSave(filteredEvents)
      toast.success({
        text:
          selectedGps.length > 0 && selectedTachograph.length > 0
            ? t('newDelegation.borders-imported')
            : selectedTachograph.length > 0
              ? t('newDelegation.tachograph-events-created')
              : t('newDelegation.gps-events-created'),
      })
    } else {
      toast.warning({
        text: t('newDelegation.no-events-created'),
      })
    }
  }

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-bg-base-50">
      {/* Header with Save/Cancel */}
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-bg-white tw-px-6 tw-py-3">
        <span className="tw-text-sm tw-font-medium tw-text-content-base">
          {t('newDelegation.import-gps')}
        </span>
        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary/90"
          >
            {t('common.save')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="tw-border-b tw-border-base-300 tw-bg-white tw-px-6 tw-py-4">
        <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-6">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="sourceFromTachograph"
              checked={sources.tacho}
              onChange={(e) => setSource('tacho', e.target.checked)}
            />
            <label
              htmlFor="sourceFromTachograph"
              className="tw-text-sm tw-text-content-base tw-opacity-70 tw-m-0"
            >
              {t('generatedTripCountrySourceType.FromTacho')}
            </label>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="sourceFromGps"
              checked={sources.gps}
              onChange={(e) => setSource('gps', e.target.checked)}
            />
            <label
              htmlFor="sourceFromGps"
              className="tw-text-sm tw-text-content-base tw-opacity-70 tw-m-0"
            >
              {t('generatedTripCountrySourceType.FromGps')}
            </label>
          </div>
        </div>
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-4">
          <DateField
            label={t('newDelegation.dateFrom') }
            value={dateFrom}
            setValue={setDateFrom}
            placeholder="YYYY-MM-DD"
          />
          <DateField
            label={t('newDelegation.dateTo') }
            value={dateTo}
            setValue={setDateTo}
            placeholder="YYYY-MM-DD"
          />
        </div>
      </div>

      {/* Table */}
      <div className="tw-flex-1 tw-overflow-y-auto tw-bg-white">
        {isLoading ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <span>{t('common.loading')}</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <span>{t('businessTrip.no-data') }</span>
          </div>
        ) : (
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full">
              <TableHeader
                columns={GpsColumns}
                toggleAll={handleToggleAll}
                isSelectedAny={allChecked}
                sortConfig={null}
              />
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.key}
                    className="tw-border-b tw-border-base-200 hover:tw-bg-base-50"
                  >
                    <td className="tw-px-4 tw-py-2">
                      <Checkbox
                        id={`import_${row.key}`}
                        checked={row.checked}
                        onChange={(e) => handleToggleItem(row.key, e.target.checked)}
                      />
                    </td>
                    <TableTextRow>
                      {row.source === 'gps'
                        ? t('generatedTripCountrySourceType.FromGps')
                        : t('generatedTripCountrySourceType.FromTacho')}
                    </TableTextRow>
                    <TableTextRow>
                      {row.source === 'gps'
                        ? getGpsDataTypeName(row.gps.type, t) ||
                          t('common.unknown') ||
                          'Nieznany'
                        : '-'}
                    </TableTextRow>
                    <TableTextRow>
                      {row.source === 'gps'
                        ? row.gps.arriveDate
                          ? moment(row.gps.arriveDate).format('DD-MM-YYYY HH:mm')
                          : '-'
                        : row.tacho.isArrival && row.tacho.date
                          ? moment(row.tacho.date).format('DD-MM-YYYY HH:mm')
                          : '-'}
                    </TableTextRow>
                    <TableTextRow>
                      {row.source === 'gps'
                        ? row.gps.leaveDate
                          ? moment(row.gps.leaveDate).format('DD-MM-YYYY HH:mm')
                          : '-'
                        : !row.tacho.isArrival && row.tacho.date
                          ? moment(row.tacho.date).format('DD-MM-YYYY HH:mm')
                          : '-'}
                    </TableTextRow>
                    <TableTextRow>
                      {(row.source === 'gps'
                        ? row.gps.foreignCountry?.name
                        : row.tacho.country?.name) || '-'}
                    </TableTextRow>
                    <TableTextRow>
                      {(row.source === 'gps' ? row.gps.city : null) || '-'}
                    </TableTextRow>
                    <TableTextRow>
                      {(
                        row.source === 'gps'
                          ? row.gps.transit
                          : row.tacho.isTransit
                      )
                        ? t('common.yes')
                        : t('common.no')}
                    </TableTextRow>
                    <TableTextRow>
                      {row.source === 'gps'
                        ? getGpsDataStatusName(row.gps.status, t) ||
                          t('common.unknown') ||
                          'Nieznany'
                        : '-'}
                    </TableTextRow>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default BusinessTripEditGpsView
